import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Bed, Bath, Maximize, Home, Heart, Share2, 
  ChevronLeft, ChevronRight, X, Check, Phone, Mail
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getProperty, toggleSavedProperty, getSavedProperties, createInquiry } from '@/lib/api';
import { buildDisplayName } from '@/lib/propertyUtils';
import { prettifyAmenity } from '@/lib/amenityLabels';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PropertyDetailPage = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { formatPrice, formatPropertyPrice } = useCurrency();

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await getProperty(slug);
        setProperty(res.data);
        
        // Check if saved
        const savedRes = await getSavedProperties();
        setIsSaved(savedRes.data.saved_ids?.includes(res.data.id));
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('PropertyDetail fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [slug]);

  const handleSaveToggle = async () => {
    try {
      const res = await toggleSavedProperty(property.id);
      setIsSaved(res.data.saved);
      toast.success(res.data.saved ? 'Property saved!' : 'Property removed from saved');
      window.dispatchEvent(new Event('savedPropertyChange'));
    } catch (err) {
      toast.error('Failed to save property');
    }
  };

  const displayName = property ? (property.display_name || buildDisplayName(property)) : 'Bali Villa';

  const handleShare = async () => {
    try {
      await navigator.share({
        title: displayName,
        text: property.short_description,
        url: window.location.href,
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createInquiry({
        ...inquiryForm,
        property_id: property.id,
        // Internal name & listing_code are auto-attached server-side from property_id.
        inquiry_type: 'property',
      });
      toast.success('Inquiry sent! We\'ll get back to you soon.');
      setInquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Amenity labels imported from centralized module (lib/amenityLabels)

  const statusLabel = {
    for_sale: 'For Sale',
    for_rent: 'For Rent',
    managed: 'Managed by Lalita',
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ubud-jungle"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-charcoal mb-4">Property Not Found</h1>
          <Link to="/properties" className="text-ubud-jungle hover:underline">
            Browse all properties
          </Link>
        </div>
      </div>
    );
  }

  const images = property.images?.length ? property.images : [property.cover_image || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200'];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="property-detail-page">
      {/* Image Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={() => setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))}
            className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <img
            src={images[currentImageIndex]}
            alt={`${displayName} - Image ${currentImageIndex + 1}`}
            className="max-w-full max-h-[90vh] object-contain"
          />
          <button
            onClick={() => setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1))}
            className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="absolute bottom-4 text-white text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}

      {/* Hero Image Section */}
      <section className="relative">
        {images.length === 1 ? (
          /* Single image layout */
          <div 
            className="h-[50vh] min-h-[400px] max-h-[500px] cursor-pointer relative group"
            onClick={() => setShowGallery(true)}
          >
            <img
              src={images[0]}
              alt={displayName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          </div>
        ) : images.length === 2 ? (
          /* Two images layout */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 h-[50vh] min-h-[400px] max-h-[500px]">
            <div 
              className="cursor-pointer relative group"
              onClick={() => setShowGallery(true)}
            >
              <img
                src={images[0]}
                alt={displayName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>
            <div
              className="cursor-pointer relative group"
              onClick={() => {
                setCurrentImageIndex(1);
                setShowGallery(true);
              }}
            >
              <img
                src={images[1]}
                alt={`${displayName} - 2`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>
          </div>
        ) : images.length === 3 ? (
          /* Three images layout - main left, 2 stacked right */
          <div className="flex flex-col md:flex-row gap-1 h-[40vh] md:h-[50vh] min-h-[300px] md:min-h-[400px] max-h-[500px]">
            <div 
              className="flex-1 md:w-1/2 md:flex-none cursor-pointer relative group overflow-hidden"
              onClick={() => setShowGallery(true)}
            >
              <img
                src={images[0]}
                alt={displayName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>
            <div className="hidden md:flex md:w-1/2 flex-col gap-1">
              <div
                className="h-1/2 cursor-pointer relative group overflow-hidden"
                onClick={() => {
                  setCurrentImageIndex(1);
                  setShowGallery(true);
                }}
              >
                <img
                  src={images[1]}
                  alt={`${displayName} - 2`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
              <div
                className="h-1/2 cursor-pointer relative group overflow-hidden"
                onClick={() => {
                  setCurrentImageIndex(2);
                  setShowGallery(true);
                }}
              >
                <img
                  src={images[2]}
                  alt={`${displayName} - 3`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
            </div>
          </div>
        ) : (
          /* Four or more images layout - main left, 4 grid right */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 h-[50vh] min-h-[400px] max-h-[500px]">
            <div 
              className="cursor-pointer relative group"
              onClick={() => setShowGallery(true)}
            >
              <img
                src={images[0]}
                alt={displayName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>
            <div className="grid grid-cols-2 grid-rows-2 gap-1">
              {images.slice(1, 5).map((img, idx) => (
                <div
                  key={img}
                  className="cursor-pointer relative group"
                  onClick={() => {
                    setCurrentImageIndex(idx + 1);
                    setShowGallery(true);
                  }}
                >
                  <img
                    src={img}
                    alt={`${displayName} - ${idx + 2}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  {idx === 3 && images.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-medium">+{images.length - 5} more</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* View All Photos Button */}
        <button
          onClick={() => setShowGallery(true)}
          className="absolute bottom-4 right-4 bg-white text-charcoal px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow font-medium text-sm"
          data-testid="view-all-photos-btn"
        >
          View All Photos ({images.length})
        </button>
      </section>

      {/* Content */}
      <div className="bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 order-2 lg:order-1">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                      property.status === 'for_sale' ? 'bg-ubud-jungle text-white' :
                      property.status === 'for_rent' ? 'bg-temple-gold text-white' :
                      'bg-charcoal-light text-white'
                    }`}>
                      {statusLabel[property.status]}
                    </span>
                    <span className="text-charcoal-muted text-sm capitalize">{property.property_type}</span>
                    {property.listing_code && (
                      <span className="font-mono text-xs bg-warm-sand text-charcoal-muted px-2 py-0.5 rounded">
                        {property.listing_code}
                      </span>
                    )}
                  </div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-2">
                    {displayName}
                  </h1>
                  <div className="flex items-center gap-2 text-charcoal-light">
                    <MapPin className="w-4 h-4" />
                    <span>{property.address || property.location}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveToggle}
                    className={`p-3 rounded-full border transition-colors ${
                      isSaved ? 'bg-red-50 border-red-200' : 'hover:bg-warm-sand border-gray-200'
                    }`}
                    data-testid="detail-save-btn"
                  >
                    <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-600 text-red-600' : 'text-charcoal'}`} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-3 rounded-full border border-gray-200 hover:bg-warm-sand transition-colors"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-5 h-5 text-charcoal" />
                  </button>
                </div>
              </div>

              {/* Price & Key Details */}
              <div className="flex flex-wrap items-baseline gap-4 mb-6">
                {(() => {
                  const status = property.status;
                  // Short-term / nightly rentals
                  if (status === 'for_rent_short' || (status === 'for_rent' && property.rental_price)) {
                    return (
                      <>
                        <span className="font-heading text-3xl font-semibold text-charcoal">
                          {formatPropertyPrice(property.rental_price, property.rental_price_idr)}
                        </span>
                        <span className="text-charcoal-muted">/ night</span>
                      </>
                    );
                  }
                  // Long-term rentals — show BOTH monthly and yearly prices
                  // when both exist (fixes the PDF-flagged bug where only
                  // one was shown).
                  if (status === 'for_rent_long' || status === 'for_rent') {
                    const hasMonthly = property.monthly_rental_price || property.monthly_rental_price_idr;
                    const hasYearly = property.yearly_rental_price || property.yearly_rental_price_idr;
                    if (hasMonthly && hasYearly) {
                      return (
                        <div className="flex flex-col gap-1" data-testid="rental-both-prices">
                          <div className="flex items-baseline gap-2">
                            <span className="font-heading text-3xl font-semibold text-charcoal">
                              {formatPropertyPrice(property.monthly_rental_price, property.monthly_rental_price_idr)}
                            </span>
                            <span className="text-charcoal-muted">/ month</span>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-heading text-xl font-medium text-charcoal-light">
                              {formatPropertyPrice(property.yearly_rental_price, property.yearly_rental_price_idr)}
                            </span>
                            <span className="text-charcoal-muted">/ year</span>
                          </div>
                        </div>
                      );
                    }
                    if (hasMonthly) {
                      return (
                        <>
                          <span className="font-heading text-3xl font-semibold text-charcoal">
                            {formatPropertyPrice(property.monthly_rental_price, property.monthly_rental_price_idr)}
                          </span>
                          <span className="text-charcoal-muted">/ month</span>
                        </>
                      );
                    }
                    if (hasYearly) {
                      return (
                        <>
                          <span className="font-heading text-3xl font-semibold text-charcoal">
                            {formatPropertyPrice(property.yearly_rental_price, property.yearly_rental_price_idr)}
                          </span>
                          <span className="text-charcoal-muted">/ year</span>
                        </>
                      );
                    }
                  }
                  // Sale / yearly lease
                  return (
                    <>
                      <span className="font-heading text-3xl font-semibold text-charcoal">
                        {formatPropertyPrice(property.price, property.price_idr)}
                      </span>
                      {property.ownership_type === 'yearly_rental' && (
                        <span className="text-charcoal-muted">/ year</span>
                      )}
                    </>
                  );
                })()}
              </div>

              {property.property_type === 'land' && property.price > 0 && property.land_size > 0 && (
                <div className="text-charcoal-light text-sm" data-testid="land-price-per-are-detail">
                  {formatPrice((property.price / property.land_size) * 100)} per 100 m² (1 are)
                </div>
              )}

              <div className="flex flex-wrap gap-6 text-charcoal">
                {property.property_type !== 'land' && (
                  <>
                    <div className="flex items-center gap-2">
                      <Bed className="w-5 h-5 text-ubud-jungle" />
                      <span className="font-medium">{property.bedrooms}</span>
                      <span className="text-charcoal-light">Bedrooms</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bath className="w-5 h-5 text-ubud-jungle" />
                      <span className="font-medium">{property.bathrooms}</span>
                      <span className="text-charcoal-light">Bathrooms</span>
                    </div>
                  </>
                )}
                {property.building_size > 0 && (
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-ubud-jungle" />
                    <span className="font-medium">{property.building_size}</span>
                    <span className="text-charcoal-light">m² Building</span>
                  </div>
                )}
                {property.land_size > 0 && (
                  <div className="flex items-center gap-2">
                    <Maximize className="w-5 h-5 text-ubud-jungle" />
                    <span className="font-medium">{property.land_size}</span>
                    <span className="text-charcoal-light">m² Land</span>
                  </div>
                )}
              </div>

              {/* For-sale specifics: Zoning + Certificate of Ownership (any property type) + Road Access (land) */}
              {property.status === 'for_sale' && (property.zoning || property.certificate_of_ownership || property.road_access) && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="property-extra-details">
                  {property.zoning && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warm-sand text-charcoal text-xs font-medium capitalize">
                      <span className={`w-2 h-2 rounded-full ${{
                        residential: 'bg-yellow-400',
                        tourism: 'bg-pink-400',
                        mixed: 'bg-orange-400',
                        agricultural: 'bg-green-500',
                        green_zone: 'bg-green-500',
                        unknown: 'bg-gray-300',
                      }[property.zoning] || 'bg-gray-300'}`} />
                      Zoning: {property.zoning === 'mixed' ? 'Mixed Use' : property.zoning.replace(/_/g, ' ')}
                    </span>
                  )}
                  {property.certificate_of_ownership && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-warm-sand text-charcoal text-xs font-medium" data-testid="certificate-badge">
                      Certificate: {property.certificate_of_ownership === 'other' ? 'Other' : property.certificate_of_ownership}
                    </span>
                  )}
                  {property.road_access && property.property_type === 'land' && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-warm-sand text-charcoal text-xs font-medium capitalize">
                      Road: {property.road_access.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              )}
            </motion.div>

            {/* Leasehold details — only rendered when the property is a
                leasehold or dual freehold-and-leasehold sale AND lease info
                exists. Surfaces the info the PDF revision brief flagged as
                missing on the deployed site. */}
            {property.status === 'for_sale' &&
             (property.ownership_type === 'leasehold' || property.ownership_type === 'both') &&
             (property.lease_years_remaining || property.lease_expiry_year || property.lease_extendable) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="mb-8 p-5 bg-warm-sand/60 rounded-2xl border border-warm-sand"
                data-testid="leasehold-details"
              >
                <h3 className="font-heading text-lg font-medium text-charcoal mb-3">
                  Leasehold Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  {property.lease_years_remaining ? (
                    <div>
                      <div className="text-charcoal-muted text-xs uppercase tracking-wider mb-1">Years Remaining</div>
                      <div className="font-heading text-lg text-charcoal">{property.lease_years_remaining}</div>
                    </div>
                  ) : null}
                  {property.lease_expiry_year ? (
                    <div>
                      <div className="text-charcoal-muted text-xs uppercase tracking-wider mb-1">Expiry Year</div>
                      <div className="font-heading text-lg text-charcoal">{property.lease_expiry_year}</div>
                    </div>
                  ) : null}
                  {property.lease_extendable ? (
                    <div>
                      <div className="text-charcoal-muted text-xs uppercase tracking-wider mb-1">Extendable</div>
                      <div className="font-heading text-lg text-charcoal">Yes</div>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            )}

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              <h2 className="font-heading text-2xl font-medium text-charcoal mb-4">About This Property</h2>
              <div className="prose prose-lg max-w-none text-charcoal-light font-body whitespace-pre-line">
                {property.description}
              </div>
            </motion.div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-12"
              >
                <h2 className="font-heading text-2xl font-medium text-charcoal mb-4">
                  {property.property_type === 'land' ? 'Land Features' : 'Amenities'}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {property.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-3 p-3 bg-warm-sand rounded-lg">
                      <Check className="w-5 h-5 text-ubud-jungle" />
                      <span className="text-charcoal font-body">{prettifyAmenity(amenity)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Map */}
            {property.latitude && property.longitude && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-12"
              >
                <h2 className="font-heading text-2xl font-medium text-charcoal mb-4">Location</h2>
                <div className="h-80 rounded-2xl overflow-hidden">
                  <MapContainer
                    center={[property.latitude, property.longitude]}
                    zoom={14}
                    scrollWheelZoom={false}
                    className="h-full w-full"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[property.latitude, property.longitude]}>
                      <Popup>{displayName}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar - Inquiry Form */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-warm-sand p-6 rounded-2xl lg:sticky lg:top-28"
            >
              <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
                Interested in this property?
              </h3>
              <p className="text-charcoal-light text-sm mb-6">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form onSubmit={handleInquirySubmit} className="space-y-4" data-testid="property-inquiry-form">
                <div>
                  <Input
                    placeholder="Your Name"
                    value={inquiryForm.name}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="bg-white"
                    data-testid="inquiry-name"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="bg-white"
                    data-testid="inquiry-email"
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Phone Number (Optional)"
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-white"
                    data-testid="inquiry-phone"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Your Message"
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    required
                    rows={4}
                    className="bg-white resize-none"
                    data-testid="inquiry-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full py-3"
                  data-testid="inquiry-submit-btn"
                >
                  {submitting ? 'Sending...' : 'Send Inquiry'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-charcoal/10">
                <p className="text-charcoal text-sm font-medium mb-3">Or contact us directly:</p>
                <div className="space-y-2">
                  <a href="tel:+6281234567890" className="flex items-center gap-2 text-charcoal-light hover:text-ubud-jungle text-sm">
                    <Phone className="w-4 h-4" />
                    +62 812 3456 7890
                  </a>
                  <a href="mailto:hello@lalita.id" className="flex items-center gap-2 text-charcoal-light hover:text-ubud-jungle text-sm">
                    <Mail className="w-4 h-4" />
                    hello@lalita.id
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
